import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import LoadingFallback from '../components/LoadingFallback';
import StatusChip from '../components/StatusChip';
import ImpactDeleteDialog, { type ImpactReport } from '../components/ImpactDeleteDialog';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type {
  EventCategory,
  EventType,
  Meet,
  Org,
  Student,
  User,
  UserRole,
  Venue,
} from '../api/types';

interface AdminOrg extends Org {
  plan?: string;
  created_at?: string;
  user_count?: number;
  student_count?: number;
  meet_count?: number;
}

interface AdminUser extends User {
  org_name?: string | null;
  created_at?: string;
}

interface AdminStudent extends Student {
  org_name?: string | null;
}

interface AdminVenue extends Venue {
  id: number;
  org_id: number;
  org_name?: string | null;
}

interface AdminMeet extends Meet {
  org_id: number;
  org_name?: string | null;
}

interface AdminEventType extends EventType {
  org_name?: string | null;
}

interface WaitlistRow {
  id: number;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  org_name?: string | null;
  source?: string | null;
  created_at?: string | null;
  approved_at?: string | null;
  redeemed_at?: string | null;
  status: 'pending' | 'approved' | 'redeemed';
  signup_url?: string | null;
}

interface Stats {
  orgs?: number;
  users?: number;
  students?: number;
  venues?: number;
  meets?: number;
  meet_events?: number;
  registrations?: number;
  results?: number;
  event_types?: number;
  invites?: number;
  notifications?: number;
  api_keys?: number;
  waitlist_signups?: number;
  users_by_role?: Record<string, number>;
  orgs_by_plan?: Record<string, number>;
}

const TAB_KEYS = [
  'overview',
  'orgs',
  'users',
  'students',
  'venues',
  'meets',
  'event-types',
  'waitlist',
] as const;
type TabKey = (typeof TAB_KEYS)[number];

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'super_admin', label: 'Super admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'coach', label: 'Coach' },
  { value: 'parent', label: 'Parent' },
  { value: 'student', label: 'Student' },
];

const ORG_PLAN_OPTIONS = [
  { value: 'trial', label: 'Trial' },
  { value: 'founding', label: 'Founding' },
];

const ORG_TYPE_OPTIONS = ['school', 'club', 'district'];

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

interface DeleteState {
  endpoint: string;
  title: string;
  entityLabel: string;
  invalidate: string[];
}

export default function SuperAdminPage() {
  const [tab, setTab] = useState<TabKey>(() => {
    const fromHash = window.location.hash.replace('#', '') as TabKey;
    return TAB_KEYS.includes(fromHash) ? fromHash : 'overview';
  });

  useEffect(() => {
    window.location.hash = tab;
  }, [tab]);

  return (
    <Box>
      <PageHeader
        title="Super admin"
        subtitle="Cross-org administration. With great power comes great cleanup."
      />
      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as TabKey)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab value="overview" label="Overview" id="sa-tab-overview" />
          <Tab value="orgs" label="Orgs" id="sa-tab-orgs" />
          <Tab value="users" label="Users" id="sa-tab-users" />
          <Tab value="students" label="Students" id="sa-tab-students" />
          <Tab value="venues" label="Venues" id="sa-tab-venues" />
          <Tab value="meets" label="Meets" id="sa-tab-meets" />
          <Tab value="event-types" label="Event types" id="sa-tab-event-types" />
          <Tab value="waitlist" label="Waitlist" id="sa-tab-waitlist" />
        </Tabs>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {tab === 'overview' && <OverviewTab />}
          {tab === 'orgs' && <OrgsTab />}
          {tab === 'users' && <UsersTab />}
          {tab === 'students' && <StudentsTab />}
          {tab === 'venues' && <VenuesTab />}
          {tab === 'meets' && <MeetsTab />}
          {tab === 'event-types' && <EventTypesTab />}
          {tab === 'waitlist' && <WaitlistTab />}
        </CardContent>
      </Card>
    </Box>
  );
}

function useDelete() {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [state, setState] = useState<DeleteState | null>(null);
  const [impact, setImpact] = useState<ImpactReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async (s: DeleteState) => {
    setState(s);
    setImpact(null);
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.get(`${s.endpoint}/impact`);
      setImpact(data);
    } catch (e) {
      setError(apiErrorMessage(e));
      setImpact({ cascade: {}, orphan: {} });
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (!state) return;
    setBusy(true);
    try {
      await api.delete(state.endpoint);
      toast({ message: `${state.entityLabel} deleted`, severity: 'success' });
      state.invalidate.forEach((key) => qc.invalidateQueries({ queryKey: [key] }));
      setState(null);
    } catch (e) {
      toast({ message: apiErrorMessage(e), severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    if (busy) return;
    setState(null);
  };

  const dialog = state && (
    <ImpactDeleteDialog
      open
      title={state.title}
      entityLabel={state.entityLabel}
      loading={loading}
      busy={busy}
      impact={impact}
      error={error}
      onCancel={cancel}
      onConfirm={confirm}
    />
  );

  return { open, dialog };
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewTab() {
  const { data, isLoading, refetch, isFetching } = useQuery<Stats>({
    queryKey: ['sa-stats'],
    queryFn: async () => (await api.get('/super-admin/stats')).data,
  });

  if (isLoading) return <LoadingFallback />;
  if (!data) return <Alert severity="error">Failed to load stats.</Alert>;

  const totals: { label: string; value: number | undefined }[] = [
    { label: 'Orgs', value: data.orgs },
    { label: 'Users', value: data.users },
    { label: 'Students', value: data.students },
    { label: 'Venues', value: data.venues },
    { label: 'Meets', value: data.meets },
    { label: 'Meet events', value: data.meet_events },
    { label: 'Registrations', value: data.registrations },
    { label: 'Results', value: data.results },
    { label: 'Event types', value: data.event_types },
    { label: 'Invites', value: data.invites },
    { label: 'Notifications', value: data.notifications },
    { label: 'API keys', value: data.api_keys },
    { label: 'Waitlist signups', value: data.waitlist_signups },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label="Refresh stats"
        >
          Refresh
        </Button>
      </Stack>
      <Grid container spacing={2}>
        {totals.map((t) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={t.label}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {t.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {(t.value ?? 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Users by role
              </Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                {Object.entries(data.users_by_role ?? {}).map(([role, count]) => (
                  <Chip key={role} label={`${role}: ${count}`} />
                ))}
                {Object.keys(data.users_by_role ?? {}).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No users yet.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Orgs by plan
              </Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                {Object.entries(data.orgs_by_plan ?? {}).map(([plan, count]) => (
                  <Chip key={plan} color="primary" variant="outlined" label={`${plan}: ${count}`} />
                ))}
                {Object.keys(data.orgs_by_plan ?? {}).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No orgs yet.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Orgs
// ---------------------------------------------------------------------------

function OrgsTab() {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const del = useDelete();
  const { data, isLoading } = useQuery<AdminOrg[]>({
    queryKey: ['sa-orgs'],
    queryFn: async () => (await api.get('/super-admin/orgs')).data,
  });
  const [editing, setEditing] = useState<AdminOrg | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['sa-orgs'] });

  return (
    <Stack spacing={2}>
      <Toolbar
        right={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            aria-label="Create org"
          >
            New org
          </Button>
        }
      />
      {isLoading ? (
        <LoadingFallback />
      ) : !data || data.length === 0 ? (
        <EmptyState title="No orgs yet" />
      ) : (
        <ResponsiveTable
          ariaLabel="Orgs"
          headers={['ID', 'Name', 'Type', 'Plan', 'Timezone', 'Users', 'Students', 'Meets', '']}
          rows={data.map((org) => ({
            key: org.id,
            cells: [
              org.id,
              <strong key="name">{org.name}</strong>,
              <Chip key="type" size="small" label={org.type} />,
              <Chip
                key="plan"
                size="small"
                color={org.plan === 'founding' ? 'success' : 'default'}
                label={org.plan ?? '—'}
              />,
              <span key="tz">{org.timezone}</span>,
              org.user_count ?? 0,
              org.student_count ?? 0,
              org.meet_count ?? 0,
              <RowActions
                key="actions"
                onEdit={() => setEditing(org)}
                onDelete={() =>
                  del.open({
                    endpoint: `/super-admin/orgs/${org.id}`,
                    title: `Delete org "${org.name}"?`,
                    entityLabel: 'Org',
                    invalidate: ['sa-orgs', 'sa-stats', 'sa-users', 'sa-students', 'sa-venues', 'sa-meets'],
                  })
                }
                ariaName={org.name}
              />,
            ],
          }))}
        />
      )}
      {(creating || editing) && (
        <OrgFormDialog
          org={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            refresh();
            toast({ message: 'Saved', severity: 'success' });
          }}
        />
      )}
      {del.dialog}
    </Stack>
  );
}

function OrgFormDialog({
  org,
  onClose,
  onSaved,
}: {
  org: AdminOrg | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(org?.name ?? '');
  const [type, setType] = useState(org?.type ?? 'school');
  const [tz, setTz] = useState(org?.timezone ?? 'UTC');
  const [plan, setPlan] = useState(org?.plan ?? 'trial');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { name, type, timezone: tz, plan };
      if (org) return (await api.patch(`/super-admin/orgs/${org.id}`, payload)).data;
      return (await api.post('/super-admin/orgs', payload)).data;
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="org-form-title">
      <DialogTitle id="org-form-title">{org ? `Edit "${org.name}"` : 'New org'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            inputProps={{ 'aria-label': 'Org name' }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                fullWidth
              >
                {ORG_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Plan"
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                fullWidth
              >
                {ORG_PLAN_OPTIONS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Timezone"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                fullWidth
              >
                {TIMEZONES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-label="Cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!name || save.isPending}
          aria-label={org ? 'Save org' : 'Create org'}
        >
          {save.isPending ? 'Saving…' : org ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

function UsersTab() {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const del = useDelete();
  const [search, setSearch] = useState('');
  const [orgId, setOrgId] = useState<number | ''>('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);

  const orgsQuery = useQuery<AdminOrg[]>({
    queryKey: ['sa-orgs'],
    queryFn: async () => (await api.get('/super-admin/orgs')).data,
  });
  const usersQuery = useQuery<AdminUser[]>({
    queryKey: ['sa-users', orgId, role, search],
    queryFn: async () =>
      (
        await api.get('/super-admin/users', {
          params: {
            org_id: orgId || undefined,
            role: role || undefined,
            q: search || undefined,
          },
        })
      ).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['sa-users'] });

  return (
    <Stack spacing={2}>
      <Toolbar
        left={
          <>
            <TextField
              size="small"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ 'aria-label': 'Search users' }}
              sx={{ minWidth: 240 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="user-org-filter">Org</InputLabel>
              <Select
                labelId="user-org-filter"
                label="Org"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
                aria-label="Filter users by org"
              >
                <MenuItem value="">All orgs</MenuItem>
                {(orgsQuery.data ?? []).map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="user-role-filter">Role</InputLabel>
              <Select
                labelId="user-role-filter"
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole | '')}
                aria-label="Filter users by role"
              >
                <MenuItem value="">All roles</MenuItem>
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        }
        right={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            aria-label="Create user"
          >
            New user
          </Button>
        }
      />
      {usersQuery.isLoading ? (
        <LoadingFallback />
      ) : !usersQuery.data || usersQuery.data.length === 0 ? (
        <EmptyState title="No users match" />
      ) : (
        <ResponsiveTable
          ariaLabel="Users"
          headers={['ID', 'Name', 'Email', 'Role', 'Org', 'Active', '']}
          rows={usersQuery.data.map((u) => ({
            key: u.id,
            cells: [
              u.id,
              u.full_name || '—',
              u.email,
              <Chip
                key="role"
                size="small"
                label={u.role}
                color={u.role === 'super_admin' ? 'secondary' : 'default'}
              />,
              u.org_name ?? `#${u.org_id}`,
              u.is_active ? (
                <CheckCircleIcon fontSize="small" color="success" aria-label="Active" />
              ) : (
                <CancelIcon fontSize="small" color="disabled" aria-label="Inactive" />
              ),
              <RowActions
                key="actions"
                onEdit={() => setEditing(u)}
                onDelete={() =>
                  del.open({
                    endpoint: `/super-admin/users/${u.id}`,
                    title: `Delete user "${u.full_name || u.email}"?`,
                    entityLabel: 'User',
                    invalidate: ['sa-users', 'sa-stats'],
                  })
                }
                ariaName={u.full_name || u.email}
              />,
            ],
          }))}
        />
      )}
      {(creating || editing) && (
        <UserFormDialog
          user={editing}
          orgs={orgsQuery.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            refresh();
            toast({ message: 'Saved', severity: 'success' });
          }}
        />
      )}
      {del.dialog}
    </Stack>
  );
}

function UserFormDialog({
  user,
  orgs,
  onClose,
  onSaved,
}: {
  user: AdminUser | null;
  orgs: AdminOrg[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(user?.email ?? '');
  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [role, setRole] = useState<UserRole>((user?.role as UserRole) ?? 'admin');
  const [orgId, setOrgId] = useState<number | ''>(user?.org_id ?? '');
  const [isActive, setIsActive] = useState<boolean>(user?.is_active ?? true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        email,
        first_name: firstName,
        last_name: lastName,
        phone: phone || null,
        role,
        org_id: orgId || undefined,
        is_active: isActive,
      };
      if (password) payload.password = password;
      if (user) return (await api.patch(`/super-admin/users/${user.id}`, payload)).data;
      return (await api.post('/super-admin/users', payload)).data;
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  const valid = !!email && !!firstName && !!lastName && !!orgId && (user || password.length >= 8);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="user-form-title">
      <DialogTitle id="user-form-title">
        {user ? `Edit ${user.full_name || user.email}` : 'New user'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                fullWidth
                inputProps={{ 'aria-label': 'First name' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                fullWidth
                inputProps={{ 'aria-label': 'Last name' }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                inputProps={{ 'aria-label': 'Email' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                inputProps={{ 'aria-label': 'Phone' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                fullWidth
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Org"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
                required
                fullWidth
              >
                {orgs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Active"
                value={isActive ? 'yes' : 'no'}
                onChange={(e) => setIsActive(e.target.value === 'yes')}
                fullWidth
              >
                <MenuItem value="yes">Active</MenuItem>
                <MenuItem value="no">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={user ? 'New password (optional)' : 'Password'}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                helperText={user ? 'Leave blank to keep current password.' : 'Min 8 characters.'}
                inputProps={{ 'aria-label': 'Password', minLength: 8 }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-label="Cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!valid || save.isPending}
          aria-label={user ? 'Save user' : 'Create user'}
        >
          {save.isPending ? 'Saving…' : user ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Students
// ---------------------------------------------------------------------------

function StudentsTab() {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const del = useDelete();
  const [search, setSearch] = useState('');
  const [orgId, setOrgId] = useState<number | ''>('');
  const [editing, setEditing] = useState<AdminStudent | null>(null);
  const [creating, setCreating] = useState(false);

  const orgsQuery = useQuery<AdminOrg[]>({
    queryKey: ['sa-orgs'],
    queryFn: async () => (await api.get('/super-admin/orgs')).data,
  });
  const studentsQuery = useQuery<AdminStudent[]>({
    queryKey: ['sa-students', orgId, search],
    queryFn: async () =>
      (
        await api.get('/super-admin/students', {
          params: { org_id: orgId || undefined, q: search || undefined },
        })
      ).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['sa-students'] });

  return (
    <Stack spacing={2}>
      <Toolbar
        left={
          <>
            <TextField
              size="small"
              placeholder="Search name or bib…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ 'aria-label': 'Search students' }}
              sx={{ minWidth: 240 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="student-org-filter">Org</InputLabel>
              <Select
                labelId="student-org-filter"
                label="Org"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
                aria-label="Filter students by org"
              >
                <MenuItem value="">All orgs</MenuItem>
                {(orgsQuery.data ?? []).map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        }
        right={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            aria-label="Create student"
          >
            New student
          </Button>
        }
      />
      {studentsQuery.isLoading ? (
        <LoadingFallback />
      ) : !studentsQuery.data || studentsQuery.data.length === 0 ? (
        <EmptyState title="No students match" />
      ) : (
        <ResponsiveTable
          ariaLabel="Students"
          headers={['ID', 'Name', 'Org', 'Grade', 'Gender', 'Bib', 'Coaches', 'Parents', '']}
          rows={studentsQuery.data.map((s) => ({
            key: s.id,
            cells: [
              s.id,
              s.full_name,
              s.org_name ?? `#${s.org_id}`,
              s.grade ?? '—',
              s.gender ?? '—',
              s.bib_number ?? '—',
              s.coaches?.length ?? 0,
              s.parents?.length ?? 0,
              <RowActions
                key="actions"
                onEdit={() => setEditing(s)}
                onDelete={() =>
                  del.open({
                    endpoint: `/super-admin/students/${s.id}`,
                    title: `Delete student "${s.full_name}"?`,
                    entityLabel: 'Student',
                    invalidate: ['sa-students', 'sa-stats'],
                  })
                }
                ariaName={s.full_name}
              />,
            ],
          }))}
        />
      )}
      {(creating || editing) && (
        <StudentFormDialog
          student={editing}
          orgs={orgsQuery.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            refresh();
            toast({ message: 'Saved', severity: 'success' });
          }}
        />
      )}
      {del.dialog}
    </Stack>
  );
}

function StudentFormDialog({
  student,
  orgs,
  onClose,
  onSaved,
}: {
  student: AdminStudent | null;
  orgs: AdminOrg[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(student?.first_name ?? '');
  const [lastName, setLastName] = useState(student?.last_name ?? '');
  const [orgId, setOrgId] = useState<number | ''>(student?.org_id ?? '');
  const [grade, setGrade] = useState(student?.grade ?? '');
  const [gender, setGender] = useState(student?.gender ?? '');
  const [bib, setBib] = useState(student?.bib_number ?? '');
  const [dob, setDob] = useState(student?.dob ?? '');
  const [notes, setNotes] = useState(student?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        org_id: orgId || undefined,
        grade: grade || null,
        gender: gender || null,
        bib_number: bib || null,
        dob: dob || null,
        notes: notes || null,
      };
      if (student) return (await api.patch(`/super-admin/students/${student.id}`, payload)).data;
      return (await api.post('/super-admin/students', payload)).data;
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  const valid = !!firstName && !!lastName && !!orgId;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="student-form-title">
      <DialogTitle id="student-form-title">
        {student ? `Edit ${student.full_name}` : 'New student'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Org"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
                required
                fullWidth
              >
                {orgs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Bib"
                value={bib}
                onChange={(e) => setBib(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="DOB"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!valid || save.isPending}
        >
          {save.isPending ? 'Saving…' : student ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Venues
// ---------------------------------------------------------------------------

function VenuesTab() {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const del = useDelete();
  const [orgId, setOrgId] = useState<number | ''>('');
  const [editing, setEditing] = useState<AdminVenue | null>(null);
  const [creating, setCreating] = useState(false);

  const orgsQuery = useQuery<AdminOrg[]>({
    queryKey: ['sa-orgs'],
    queryFn: async () => (await api.get('/super-admin/orgs')).data,
  });
  const venuesQuery = useQuery<AdminVenue[]>({
    queryKey: ['sa-venues', orgId],
    queryFn: async () =>
      (await api.get('/super-admin/venues', { params: { org_id: orgId || undefined } })).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['sa-venues'] });

  return (
    <Stack spacing={2}>
      <Toolbar
        left={
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="venue-org-filter">Org</InputLabel>
            <Select
              labelId="venue-org-filter"
              label="Org"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
              aria-label="Filter venues by org"
            >
              <MenuItem value="">All orgs</MenuItem>
              {(orgsQuery.data ?? []).map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
        right={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            aria-label="Create venue"
          >
            New venue
          </Button>
        }
      />
      {venuesQuery.isLoading ? (
        <LoadingFallback />
      ) : !venuesQuery.data || venuesQuery.data.length === 0 ? (
        <EmptyState title="No venues match" />
      ) : (
        <ResponsiveTable
          ariaLabel="Venues"
          headers={['ID', 'Name', 'Org', 'City', 'Region', '']}
          rows={venuesQuery.data.map((v) => ({
            key: v.id,
            cells: [
              v.id,
              v.name,
              v.org_name ?? `#${v.org_id}`,
              v.city ?? '—',
              v.region ?? '—',
              <RowActions
                key="actions"
                onEdit={() => setEditing(v)}
                onDelete={() =>
                  del.open({
                    endpoint: `/super-admin/venues/${v.id}`,
                    title: `Delete venue "${v.name}"?`,
                    entityLabel: 'Venue',
                    invalidate: ['sa-venues', 'sa-stats', 'sa-meets'],
                  })
                }
                ariaName={v.name}
              />,
            ],
          }))}
        />
      )}
      {(creating || editing) && (
        <VenueFormDialog
          venue={editing}
          orgs={orgsQuery.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            refresh();
            toast({ message: 'Saved', severity: 'success' });
          }}
        />
      )}
      {del.dialog}
    </Stack>
  );
}

function VenueFormDialog({
  venue,
  orgs,
  onClose,
  onSaved,
}: {
  venue: AdminVenue | null;
  orgs: AdminOrg[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(venue?.name ?? '');
  const [orgId, setOrgId] = useState<number | ''>(venue?.org_id ?? '');
  const [address, setAddress] = useState(venue?.address ?? '');
  const [city, setCity] = useState(venue?.city ?? '');
  const [region, setRegion] = useState(venue?.region ?? '');
  const [postalCode, setPostalCode] = useState(venue?.postal_code ?? '');
  const [notes, setNotes] = useState(venue?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        org_id: orgId || undefined,
        address: address || null,
        city: city || null,
        region: region || null,
        postal_code: postalCode || null,
        notes: notes || null,
      };
      if (venue) return (await api.patch(`/super-admin/venues/${venue.id}`, payload)).data;
      return (await api.post('/super-admin/venues', payload)).data;
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  const valid = !!name && !!orgId;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="venue-form-title">
      <DialogTitle id="venue-form-title">{venue ? `Edit "${venue.name}"` : 'New venue'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Org"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
                required
                fullWidth
              >
                {orgs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Postal code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!valid || save.isPending}
        >
          {save.isPending ? 'Saving…' : venue ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Meets
// ---------------------------------------------------------------------------

function MeetsTab() {
  const del = useDelete();
  const [orgId, setOrgId] = useState<number | ''>('');
  const orgsQuery = useQuery<AdminOrg[]>({
    queryKey: ['sa-orgs'],
    queryFn: async () => (await api.get('/super-admin/orgs')).data,
  });
  const meetsQuery = useQuery<AdminMeet[]>({
    queryKey: ['sa-meets', orgId],
    queryFn: async () =>
      (await api.get('/super-admin/meets', { params: { org_id: orgId || undefined } })).data,
  });

  return (
    <Stack spacing={2}>
      <Toolbar
        left={
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="meet-org-filter">Org</InputLabel>
            <Select
              labelId="meet-org-filter"
              label="Org"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
              aria-label="Filter meets by org"
            >
              <MenuItem value="">All orgs</MenuItem>
              {(orgsQuery.data ?? []).map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />
      <Alert severity="info">
        Editing meet contents (events, registrations, results) happens in the org's regular meet
        pages. Use this view for cross-org visibility and emergency cleanup.
      </Alert>
      {meetsQuery.isLoading ? (
        <LoadingFallback />
      ) : !meetsQuery.data || meetsQuery.data.length === 0 ? (
        <EmptyState title="No meets match" />
      ) : (
        <ResponsiveTable
          ariaLabel="Meets"
          headers={['ID', 'Name', 'Org', 'Status', 'Dates', 'Venue', 'Events', 'Registrations', '']}
          rows={meetsQuery.data.map((m) => ({
            key: m.id,
            cells: [
              m.id,
              m.name,
              m.org_name ?? `#${m.org_id}`,
              <StatusChip key="status" status={m.status} />,
              `${dayjs(m.start_date).format('MMM D, YYYY')} – ${dayjs(m.end_date).format('MMM D, YYYY')}`,
              m.venue?.name ?? '—',
              m.event_count ?? 0,
              m.registration_count ?? 0,
              <RowActions
                key="actions"
                onDelete={() =>
                  del.open({
                    endpoint: `/super-admin/meets/${m.id}`,
                    title: `Delete meet "${m.name}"?`,
                    entityLabel: 'Meet',
                    invalidate: ['sa-meets', 'sa-stats'],
                  })
                }
                ariaName={m.name}
              />,
            ],
          }))}
        />
      )}
      {del.dialog}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

function EventTypesTab() {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const del = useDelete();
  const [editing, setEditing] = useState<AdminEventType | null>(null);
  const [creating, setCreating] = useState(false);

  const orgsQuery = useQuery<AdminOrg[]>({
    queryKey: ['sa-orgs'],
    queryFn: async () => (await api.get('/super-admin/orgs')).data,
  });
  const eventTypesQuery = useQuery<AdminEventType[]>({
    queryKey: ['sa-event-types'],
    queryFn: async () => (await api.get('/super-admin/event-types')).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['sa-event-types'] });
    qc.invalidateQueries({ queryKey: ['event-types'] });
  };

  return (
    <Stack spacing={2}>
      <Toolbar
        right={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            aria-label="Create event type"
          >
            New event type
          </Button>
        }
      />
      <Alert severity="info">
        Global event types are visible to every org. Org-specific event types are only visible to
        their owning org. Event types in use by a meet event can't be deleted; you can edit them or
        re-target which org owns them.
      </Alert>
      {eventTypesQuery.isLoading ? (
        <LoadingFallback />
      ) : !eventTypesQuery.data || eventTypesQuery.data.length === 0 ? (
        <EmptyState title="No event types" />
      ) : (
        <ResponsiveTable
          ariaLabel="Event types"
          headers={['ID', 'Code', 'Name', 'Category', 'Unit', 'Distance', 'Relay', 'Org', '']}
          rows={eventTypesQuery.data.map((et) => ({
            key: et.id,
            cells: [
              et.id,
              <code key="code">{et.code}</code>,
              et.name,
              <Chip key="cat" size="small" label={et.category} />,
              et.unit,
              et.distance_meters ?? '—',
              et.is_relay ? 'Yes' : 'No',
              et.org_id == null ? <Chip key="org" size="small" color="primary" label="global" /> : (et.org_name ?? `#${et.org_id}`),
              <RowActions
                key="actions"
                onEdit={() => setEditing(et)}
                onDelete={() =>
                  del.open({
                    endpoint: `/super-admin/event-types/${et.id}`,
                    title: `Delete event type "${et.name}"?`,
                    entityLabel: 'Event type',
                    invalidate: ['sa-event-types', 'sa-stats'],
                  })
                }
                ariaName={et.name}
              />,
            ],
          }))}
        />
      )}
      {(creating || editing) && (
        <EventTypeFormDialog
          eventType={editing}
          orgs={orgsQuery.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            refresh();
            toast({ message: 'Saved', severity: 'success' });
          }}
        />
      )}
      {del.dialog}
    </Stack>
  );
}

function EventTypeFormDialog({
  eventType,
  orgs,
  onClose,
  onSaved,
}: {
  eventType: AdminEventType | null;
  orgs: AdminOrg[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(eventType?.code ?? '');
  const [name, setName] = useState(eventType?.name ?? '');
  const [category, setCategory] = useState<EventCategory>((eventType?.category as EventCategory) ?? 'track');
  const [unit, setUnit] = useState(eventType?.unit ?? 'seconds');
  const [distance, setDistance] = useState(eventType?.distance_meters?.toString() ?? '');
  const [isRelay, setIsRelay] = useState<boolean>(eventType?.is_relay ?? false);
  const [orgId, setOrgId] = useState<number | 'global'>(
    eventType?.org_id == null ? 'global' : eventType.org_id,
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        category,
        unit,
        is_relay: isRelay,
        distance_meters: distance ? Number(distance) : null,
        org_id: orgId === 'global' ? null : orgId,
      };
      if (eventType) {
        return (await api.patch(`/super-admin/event-types/${eventType.id}`, payload)).data;
      }
      return (await api.post('/super-admin/event-types', payload)).data;
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  const valid = !!code.trim() && !!name.trim();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="event-type-form-title">
      <DialogTitle id="event-type-form-title">
        {eventType ? `Edit ${eventType.name}` : 'New event type'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                fullWidth
                inputProps={{ 'aria-label': 'Event code' }}
                helperText="e.g. 800M, LJ, 50FREE"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                inputProps={{ 'aria-label': 'Event name' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category"
                select
                value={category}
                onChange={(e) => setCategory(e.target.value as EventCategory)}
                fullWidth
              >
                <MenuItem value="track">Track</MenuItem>
                <MenuItem value="field">Field</MenuItem>
                <MenuItem value="relay">Relay</MenuItem>
                <MenuItem value="multi">Multi-events</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Unit"
                select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                fullWidth
              >
                <MenuItem value="seconds">Seconds (time)</MenuItem>
                <MenuItem value="meters">Meters (distance/height)</MenuItem>
                <MenuItem value="points">Points</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Distance (m, optional)"
                type="number"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Is a relay?"
                select
                value={isRelay ? 'yes' : 'no'}
                onChange={(e) => setIsRelay(e.target.value === 'yes')}
                fullWidth
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Owner"
                select
                value={orgId === 'global' ? '__global__' : String(orgId)}
                onChange={(e) =>
                  setOrgId(e.target.value === '__global__' ? 'global' : Number(e.target.value))
                }
                fullWidth
                helperText="Global event types are available to every org. Per-org events are only visible to that org."
              >
                <MenuItem value="__global__">🌍 Global (all orgs)</MenuItem>
                {orgs.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!valid || save.isPending}
          aria-label={eventType ? 'Save event type' : 'Create event type'}
        >
          {save.isPending ? 'Saving…' : eventType ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Waitlist
// ---------------------------------------------------------------------------

function WaitlistTab() {
  const qc = useQueryClient();
  const { toast, confirm } = useFeedback();
  const del = useDelete();
  const waitlistQuery = useQuery<WaitlistRow[]>({
    queryKey: ['sa-waitlist'],
    queryFn: async () => (await api.get('/super-admin/waitlist')).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['sa-waitlist'] });

  const approve = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/super-admin/waitlist/${id}/approve`)).data,
    onSuccess: () => {
      toast({ message: 'Approved & emailed', severity: 'success' });
      refresh();
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const revoke = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/super-admin/waitlist/${id}/revoke`)).data,
    onSuccess: () => {
      toast({ message: 'Revoked', severity: 'info' });
      refresh();
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const copyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    toast({ message: 'Signup URL copied', severity: 'success' });
  };

  return (
    <Stack spacing={2}>
      {waitlistQuery.isLoading ? (
        <LoadingFallback />
      ) : !waitlistQuery.data || waitlistQuery.data.length === 0 ? (
        <EmptyState title="Waitlist is empty" />
      ) : (
        <ResponsiveTable
          ariaLabel="Waitlist signups"
          headers={['Email', 'Name', 'Org', 'Status', 'Created', 'Actions', '']}
          rows={waitlistQuery.data.map((w) => ({
            key: w.id,
            cells: [
              w.email,
              `${w.first_name ?? ''} ${w.last_name ?? ''}`.trim() || '—',
              w.org_name ?? '—',
              <StatusChip key="status" status={w.status} />,
              w.created_at ? dayjs(w.created_at).format('MMM D, YYYY') : '—',
              <Stack key="actions" direction="row" spacing={1}>
                {w.status === 'pending' && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => approve.mutate(w.id)}
                    disabled={approve.isPending}
                    aria-label={`Approve ${w.email}`}
                  >
                    Approve
                  </Button>
                )}
                {w.status === 'approved' && (
                  <>
                    <Tooltip title={w.signup_url ?? ''}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => w.signup_url && copyUrl(w.signup_url)}
                          aria-label={`Copy signup URL for ${w.email}`}
                          disabled={!w.signup_url}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      onClick={() => approve.mutate(w.id)}
                      disabled={approve.isPending}
                      aria-label={`Resend invite to ${w.email}`}
                    >
                      Resend
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Revoke approval?',
                          message: `Revoke the founding-coach approval for ${w.email}?`,
                          destructive: true,
                          confirmLabel: 'Revoke',
                        });
                        if (ok) revoke.mutate(w.id);
                      }}
                      disabled={revoke.isPending}
                      aria-label={`Revoke approval for ${w.email}`}
                    >
                      Revoke
                    </Button>
                  </>
                )}
                {w.status === 'redeemed' && (
                  <Typography variant="caption" color="text.secondary">
                    Redeemed {w.redeemed_at ? dayjs(w.redeemed_at).format('MMM D') : ''}
                  </Typography>
                )}
              </Stack>,
              <RowActions
                key="row-actions"
                onDelete={() =>
                  del.open({
                    endpoint: `/super-admin/waitlist/${w.id}`,
                    title: `Delete waitlist signup "${w.email}"?`,
                    entityLabel: 'Waitlist signup',
                    invalidate: ['sa-waitlist', 'sa-stats'],
                  })
                }
                ariaName={w.email}
              />,
            ],
          }))}
        />
      )}
      {del.dialog}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Toolbar({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          {left}
        </Stack>
        <Box>{right}</Box>
      </Stack>
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

interface RowActionsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  ariaName: string;
}

function RowActions({ onEdit, onDelete, ariaName }: RowActionsProps) {
  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      {onEdit && (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit} aria-label={`Edit ${ariaName}`}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={onDelete}
            aria-label={`Delete ${ariaName}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

interface RowSpec {
  key: string | number;
  cells: ReactNode[];
}

function ResponsiveTable({
  headers,
  rows,
  ariaLabel,
}: {
  headers: ReactNode[];
  rows: RowSpec[];
  ariaLabel: string;
}) {
  const cols = useMemo(() => headers.length, [headers]);
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={ariaLabel} sx={{ minWidth: cols * 90 }}>
        <TableHead>
          <TableRow>
            {headers.map((h, i) => (
              <TableCell
                key={i}
                align={i === headers.length - 1 ? 'right' : 'left'}
                sx={{ fontWeight: 600 }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} hover>
              {r.cells.map((cell, i) => (
                <TableCell
                  key={i}
                  align={i === r.cells.length - 1 ? 'right' : 'left'}
                  sx={{ verticalAlign: 'middle' }}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
